export const calculateCustomHandicap = (
  history,
  initialHcp,
  playerName = null,
  playerId = null
) => {
  const getStdDev = (hcp) => {
    if (hcp <= 8) return 3;
    if (hcp <= 16) return 5;
    return 6;
  };

  const getDampener = (hcp) => {
    if (hcp <= 8) return 0.5;
    if (hcp <= 16) return 0.75;
    return 1.0;
  };

  const getEscMax = (hcp, par) => {
    if (hcp <= 9) return par + 2;
    if (hcp <= 19) return 7;
    if (hcp <= 29) return 8;
    if (hcp <= 39) return 9;
    return 10;
  };

  if (!history || history.length === 0) {
    return {
      current: Math.round(initialHcp),
      previous: Math.round(initialHcp),
      diff: 0,
    };
  }

  const sortedRounds = [...history].sort(
    (a, b) => new Date(a.date) - new Date(b.date)
  );

  const getScore = (round) => {
    const par =
      round.total_par ||
      round.course_data?.holes?.reduce((sum, h) => sum + h.par, 0) ||
      72;

    const holes = round.course_data?.holes || [];
    const playerScores =
      round.scores?.[playerName] ||
      (playerId ? round.scores?.[playerId] : null) ||
      {};

    // Handle quick entry format { total: N } — ESC cannot be applied
    if (playerScores.total !== undefined) {
      const quickPar =
        round.course_data?.holes?.reduce((sum, h) => sum + h.par, 0) ||
        round.total_par ||
        72;
      return { total: Number(playerScores.total), par: quickPar };
    }

    const hcp =
      round.player_handicaps?.[playerName] ||
      (playerId ? round.player_handicaps?.[playerId] : null) ||
      initialHcp ||
      0;

    const holesPlayed = holes.filter(
      (h, i) => Number(playerScores[i + 1]) > 0
    ).length;

    // Build per-hole historical averages from fully completed rounds
    const completeRounds = sortedRounds.filter((r) => {
      if (r === round) return false;
      const s = r.scores?.[playerName] || {};
      const rHoles = r.course_data?.holes || [];
      return (
        rHoles.length > 0 &&
        rHoles.filter((h, i) => Number(s[i + 1]) > 0).length >= rHoles.length
      );
    });

    const holeHistory = {};
    completeRounds.forEach((r) => {
      const s = r.scores?.[playerName] || {};
      const rHoles = r.course_data?.holes || [];
      rHoles.forEach((h, i) => {
        const hNum = i + 1;
        const score = Number(s[hNum]);
        if (score > 0) {
          if (!holeHistory[hNum]) holeHistory[hNum] = [];
          holeHistory[hNum].push(score - h.par);
        }
      });
    });

    // Overall avg diff from THIS partial round (fallback)
    const partialDiffs = holes
      .map((h, i) => ({ h, score: Number(playerScores[i + 1]) }))
      .filter(({ score }) => score > 0)
      .map(({ h, score }) => score - h.par);
    const overallAvgDiff =
      partialDiffs.length > 0
        ? partialDiffs.reduce((a, b) => a + b, 0) / partialDiffs.length
        : 0;

    let playerTotal = 0;

    if (holes.length === 0) {
      // No course data — fall back to raw sum
      playerTotal = Object.values(playerScores).reduce(
        (sum, val) => sum + (Number(val) || 0),
        0
      );
    } else if (holesPlayed >= holes.length) {
      // Full round — apply ESC capping per hole
      holes.forEach((hole, i) => {
        const holeNum = i + 1;
        const raw = Number(playerScores[holeNum]) || 0;
        if (raw > 0) {
          playerTotal += Math.min(raw, getEscMax(hcp, hole.par));
        }
      });
    } else {
      // Partial round — apply ESC to actual scores, not to filled-in averages
      holes.forEach((hole, i) => {
        const holeNum = i + 1;
        const actual = Number(playerScores[holeNum]);

        if (actual > 0) {
          playerTotal += Math.min(actual, getEscMax(hcp, hole.par));
        } else if (
          completeRounds.length >= 2 &&
          holeHistory[holeNum]?.length > 0
        ) {
          const holeDiffs = holeHistory[holeNum];
          let ema = holeDiffs[0];
          holeDiffs.forEach((diff, idx) => {
            const k = 2 / (idx + 2);
            ema = diff * k + ema * (1 - k);
          });
          playerTotal += hole.par + Math.round(ema);
        } else if (
          completeRounds.length === 1 &&
          holeHistory[holeNum]?.length > 0
        ) {
          const avg =
            holeHistory[holeNum].reduce((a, b) => a + b, 0) /
            holeHistory[holeNum].length;
          playerTotal += hole.par + Math.round(avg);
        } else if (partialDiffs.length > 0) {
          playerTotal += hole.par + Math.round(overallAvgDiff);
        } else {
          const baseStrokes = Math.floor(hcp / 18);
          const extraStroke = hole.difficulty <= hcp % 18 ? 1 : 0;
          playerTotal += hole.par + baseStrokes + extraStroke;
        }
      });
    }

    if (!playerTotal || playerTotal === 0) return null;
    return { total: playerTotal, par };
  };

  const calculateHcp = (rounds) => {
    if (rounds.length === 0) return initialHcp;

    let adjustments = [];

    rounds.forEach((round, index) => {
      const scoreData = getScore(round);
      if (!scoreData) return;

      const roundHcp =
        round.player_handicaps?.[playerName] ||
        round.player_handicaps?.[playerId] ||
        initialHcp;

      const netDiff = scoreData.total - scoreData.par - roundHcp;
      const σ = getStdDev(roundHcp);
      const dampener = getDampener(roundHcp);
      const factor = netDiff / σ;
      const adjustment = factor * σ * dampener;
      const recencyWeight = Math.pow(0.85, rounds.length - 1 - index);

      adjustments.push({ adjustment, recencyWeight });
    });

    if (adjustments.length === 0) return initialHcp;

    const totalWeight = adjustments.reduce((sum, a) => sum + a.recencyWeight, 0);
    const weightedAvgAdjustment =
      adjustments.reduce((sum, a) => sum + a.adjustment * a.recencyWeight, 0) /
      totalWeight;

    const newHcp = initialHcp + weightedAvgAdjustment;

    // Blend toward initialHcp for players with few rounds
    const blendWeight = Math.min(rounds.length / 5, 1);
    return newHcp * blendWeight + initialHcp * (1 - blendWeight);
  };

  const roundCount = sortedRounds.length;
  const blendedCurrent = calculateHcp(sortedRounds);
  const blendedPrevious =
    roundCount <= 1 ? initialHcp : calculateHcp(sortedRounds.slice(0, -1));

  const currentRounded = Math.round(blendedCurrent);
  const previousRounded = Math.round(blendedPrevious);

  return {
    current: currentRounded,
    previous: previousRounded,
    diff: currentRounded - previousRounded,
  };
};
